import '@redirecthealth/plan-factory-ui/style.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { bootstrapMsal } from './auth/bootstrap';
import { createMsalAuthAdapter } from './auth/msalAdapter';
import { createMsalInstance } from './auth/msalInstance';
import { loadRuntimeConfig } from './config';
import { createHostI18n } from './i18n';
import { createHostStore } from './store';

const container = document.getElementById('root');
if (!container) throw new Error('Missing #root element');
const root = createRoot(container);

async function start() {
  const config = await loadRuntimeConfig();
  const pca = createMsalInstance(config);
  // Asking for the plan-service scope at sign-in surfaces consent problems there.
  const loginScopes = ['openid', 'profile', config.planServiceScope];
  const i18n = createHostI18n();

  if ((await bootstrapMsal(pca, loginScopes)) === 'redirecting') {
    root.render(<p role="status">{i18n.t('signingIn')}</p>);
    return;
  }

  root.render(
    <StrictMode>
      <Provider store={createHostStore()}>
        <I18nextProvider i18n={i18n}>
          <BrowserRouter>
            <App config={config} auth={createMsalAuthAdapter(pca, loginScopes)} />
          </BrowserRouter>
        </I18nextProvider>
      </Provider>
    </StrictMode>,
  );
}

start().catch((error: unknown) => {
  // handleRedirectPromise() can reject (cancelled consent, an AADSTS error, another
  // tab mid-login): there is no account and no interaction in flight at that point,
  // so a reload is a real fix, not just a dismissal.
  const message = error instanceof Error ? error.message : String(error);
  root.render(
    <div role="alert">
      <p>The portal could not start: {message}</p>
      <button type="button" onClick={() => window.location.reload()}>
        Try again
      </button>
    </div>,
  );
});
